<template>
  <div class="chart-card">
    <div class="chart-header">
      <div class="chart-title-block">
        <span class="chart-kicker">RANKING</span>
        <h3 class="chart-title">Top Competidores</h3>
      </div>
    </div>

    <div v-if="loading" class="chart-loading">Cargando…</div>
    <div v-else-if="top.length === 0" class="chart-empty">Sin datos</div>
    <div v-else class="top-list">
      <div v-for="(c, idx) in top" :key="c.tienda" class="top-row">
        <div class="top-rank">#{{ idx + 1 }}</div>
        <div class="top-info">
          <div class="top-tienda">{{ formatStoreName(c.tienda) }}</div>
          <div class="top-meta">
            <span class="meta-badge meta-comparados">{{ c.productos_comparados }} comp.</span>
            <span class="meta-badge meta-amenaza">
              {{ c.donde_nos_ganan }} ganan ({{ c.pct_donde_nos_ganan }}%)
            </span>
          </div>
        </div>
        <div class="top-gap" :class="{ negative: c.gap_promedio < 0 }">
          <div class="gap-label">GAP $</div>
          <div class="gap-value">{{ formatGap(c.gap_promedio) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineProps } from 'vue';
import axios from 'axios';

const props = defineProps({
  vista: { type: String, default: 'web' },
  limit: { type: Number, default: 5 }
});

const API = 'https://dash-visaovip.kompara.tech/api';
const getH = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token_precios') } });

const top = ref([]);
const loading = ref(true);

const formatStoreName = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const formatGap = (g) => {
  const sign = g >= 0 ? '+' : '';
  return sign + g.toFixed(2);
};

const load = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`${API}/stats/top-competidores`, {
      ...getH(), params: { vista: props.vista, limit: props.limit }
    });
    top.value = res.data.top || [];
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

watch(() => props.vista, load);
onMounted(load);
</script>

<style scoped>
.chart-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.chart-header {
  padding: 10px 14px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.chart-kicker {
  display: block;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.chart-title {
  margin: 2px 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}
.chart-loading, .chart-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
}

.top-list {
  display: flex;
  flex-direction: column;
}
.top-row {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 10px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--color-border);
  align-items: center;
}
.top-row:last-child {
  border-bottom: none;
}
.top-row:hover {
  background: var(--color-hover-row);
}

.top-rank {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  color: var(--color-accent);
  text-align: center;
}

.top-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.top-tienda {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text);
}
.top-meta {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.meta-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 2px;
  letter-spacing: 0.03em;
  border: 1px solid;
}
.meta-comparados {
  background: var(--color-surface-3);
  color: var(--color-text-muted);
  border-color: var(--color-border-strong);
}
.meta-amenaza {
  background: var(--color-danger-bg);
  color: var(--color-danger-text);
  border-color: var(--color-danger);
}

.top-gap {
  text-align: right;
}
.gap-label {
  font-family: var(--font-mono);
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--color-text-faint);
}
.gap-value {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 700;
  color: var(--color-danger-text);
  font-variant-numeric: tabular-nums;
}
.top-gap.negative .gap-value {
  color: var(--color-success-text);
}
</style>